import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, CheckCircle2, X, Image as ImageIcon, MessageSquare, ArrowLeft, Users, Loader2, ShieldCheck } from 'lucide-react';
import { isPaymentRequired } from '@/lib/featureFlags';
import { toast } from 'sonner';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import CreativesLayout from '@/layouts/CreativesLayout';
import AspectRatioSelector from '@/components/creative-config/AspectRatioSelector';
import LogoOverlaySection from '@/components/creative-config/LogoOverlaySection';
import SelectablePhotoGrid from '@/components/creative-config/SelectablePhotoGrid';
import SelectableReviewsList from '@/components/creative-config/SelectableReviewsList';
import ScriptModeSelector from '@/components/creative-config/ScriptModeSelector';
import VoicePicker from '@/components/creatives/VoicePicker';
import CampaignBriefSection from '@/components/creatives/CampaignBriefSection';
import CharacterCard from './CharacterCard';
import PortraitCropper from './PortraitCropper';

const CHARACTER_ROLE_OPTIONS = [
    'Brand Ambassador',
    'Chef / Cook',
    'Store Owner',
    'Customer',
    'Employee / Staff',
    'Model',
    'Spokesperson',
];

const inferPhotoFormat = (photo) => {
    const photoUrl = photo?.url || photo?.data_url || photo?.photo_url || '';
    const caption = photo?.caption || '';
    const rawValue = `${photoUrl} ${caption}`.toLowerCase();

    if (rawValue.includes('image/png') || rawValue.includes('.png')) return 'PNG';
    if (rawValue.includes('image/webp') || rawValue.includes('.webp')) return 'WEBP';
    if (rawValue.includes('image/gif') || rawValue.includes('.gif')) return 'GIF';
    if (
        rawValue.includes('image/jpeg')
        || rawValue.includes('image/jpg')
        || rawValue.includes('.jpeg')
        || rawValue.includes('.jpg')
    ) return 'JPEG';

    return 'Image';
};

const ConfigureStep = ({
    realBusinessData,
    currentOrg,
    // Video format
    aspectRatio,
    setAspectRatio,
    duration,
    setDuration,
    scriptMode,
    setScriptMode,
    voiceId,
    setVoiceId,
    silentVideo,
    setSilentVideo,
    // Campaign brief
    campaignBrief,
    setCampaignBrief,
    campaignOptions,
    campaignOptionsError,
    // Photos
    photos,
    isUploadingPhoto,
    photoDeleteLoading = {},
    onPhotoToggle,
    onPhotoUpload,
    onPhotoDelete,
    // Reviews
    reviews,
    selectedReviews,
    onReviewToggle,
    // Characters
    characters = [],
    onCharacterUpload,
    onCharacterUpdate,
    onCharacterDelete,
    // Logo
    logoDataUrl,
    onLogoUpload,
    onLogoDelete,
    logoOverlayEnabled,
    setLogoOverlayEnabled,
    logoOverlayPosition,
    setLogoOverlayPosition,
    logoOverlayOpacity,
    setLogoOverlayOpacity,
    // Error & actions
    apiError,
    onBack,
    onGenerate,
    // Focused mode (deal flow)
    focusedMode = false,
    allowedAspectRatios,
    allowedDurations,
    hideReviews = false,
    generateButtonLabel,
    hideBackButton = false,
    generateDisabledOverride = false,
    generateDisabledTooltip = '',
    generateButtonLoadingLabel,
    isGenerating = false,
    title = 'Configure Video',
    description,
    headerActions = null,
    campaignBriefTopSlot = null,
    hideCharacters = false,
    hideDuration = false,
    minimumSelectedPhotos = 0,
    allowZeroSelectedPhotos = true,
    videoSettingsDisabled = false,
    logoSectionProps = {},
    layoutFullHeight = false,
    layoutStickyOffsetClassName = 'top-16',
}) => {
    const fileInputRef = useRef(null);
    const characterFileInputRef = useRef(null);
    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [previewImageMeta, setPreviewImageMeta] = useState(null);

    // Character dialog state
    const [characterDialogOpen, setCharacterDialogOpen] = useState(false);
    const [editingCharacter, setEditingCharacter] = useState(null); // null = new, object = editing
    const [characterForm, setCharacterForm] = useState({ name: '', role: '', description: '' });
    const [characterImages, setCharacterImages] = useState([]); // array of { id, dataUrl, width, height }
    const [croppingImageSrc, setCroppingImageSrc] = useState(null); // raw image being cropped
    const [isCharacterSaving, setIsCharacterSaving] = useState(false);
    const [characterRoleCustom, setCharacterRoleCustom] = useState(false);
    const addPhotoInputRef = useRef(null);
    // Fix mismatched MIME type: API sometimes returns SVG content as image/png
    const logoSrc = (() => {
        if (!logoDataUrl) return null;
        if (logoDataUrl.startsWith('data:image/png;base64,')) {
            try {
                const base64 = logoDataUrl.split(',')[1];
                const decoded = atob(base64.substring(0, 30));
                if (decoded.trimStart().startsWith('<svg') || decoded.trimStart().startsWith('<?xml')) {
                    return `data:image/svg+xml;base64,${base64}`;
                }
            } catch { /* fall through */ }
        }
        return logoDataUrl;
    })();

    const selectedPhotosCount = photos.filter((p) => p.selected).length;
    const tooFewPhotos = allowZeroSelectedPhotos
        ? selectedPhotosCount > 0 && selectedPhotosCount < minimumSelectedPhotos
        : selectedPhotosCount < minimumSelectedPhotos;
    const missingSeasonal = campaignBrief.purpose === 'seasonal' && !campaignBrief.seasonal_theme;
    const missingOffer = campaignBrief.purpose === 'promotion' && !campaignBrief.offers?.[0]?.headline?.trim();
    const localGenerateDisabled = tooFewPhotos || missingSeasonal || missingOffer;
    const localGenerateTooltip = tooFewPhotos
        ? allowZeroSelectedPhotos
            ? `Select at least ${minimumSelectedPhotos} photos, or deselect all for AI-generated visuals`
            : `Select at least ${minimumSelectedPhotos} photos`
        : missingSeasonal
            ? 'Please select a seasonal theme'
            : missingOffer
                ? 'Please enter an offer headline'
                : '';
    const generateDisabled = generateDisabledOverride || localGenerateDisabled || isGenerating;
    const generateTooltip = generateDisabledOverride ? generateDisabledTooltip : localGenerateTooltip;

    // ── Focused-mode computed values ────────────────────────────────────────
    const effectiveAspectRatios = allowedAspectRatios || ['16:9', '9:16'];
    const effectiveDurations = allowedDurations || [6, 10, 15, 30];
    const campaignBriefSummary = (() => {
        if (!campaignOptions) return 'Loading...';
        const purposeLabel = campaignOptions.purpose?.find(o => o.value === campaignBrief.purpose)?.label || 'Not set';
        const toneLabel = campaignOptions.tone?.find(o => o.value === campaignBrief.tone)?.label || 'Auto';
        return `${purposeLabel} · ${toneLabel}`;
    })();

    const charactersSummary = characters.length === 0
        ? 'None added'
        : `${characters.length} character${characters.length > 1 ? 's' : ''}`;

    const logoSummaryText = logoDataUrl ? 'Logo uploaded' : 'No logo';

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            onPhotoUpload(file);
            e.target.value = '';
        }
    };

    const handleCharacterFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (characters.length >= 3 && !editingCharacter) {
            toast.error('Maximum 3 characters allowed');
            e.target.value = '';
            return;
        }
        // Check photo limit within dialog
        if (characterDialogOpen && characterImages.length >= 3) {
            toast.error('Maximum 3 photos per character');
            e.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setCroppingImageSrc(reader.result);
            // Open dialog for new character if not already open
            if (!characterDialogOpen) {
                setCharacterForm({ name: '', role: '', description: '' });
                setCharacterRoleCustom(false);
                setEditingCharacter(null);
                setCharacterImages([]);
                setCharacterDialogOpen(true);
            }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCropComplete = (croppedDataUrl, width, height) => {
        setCharacterImages(prev => [
            ...prev,
            { id: crypto.randomUUID(), dataUrl: croppedDataUrl, width, height },
        ]);
        setCroppingImageSrc(null);
    };

    const handleCropCancel = () => {
        setCroppingImageSrc(null);
        // If no images were added yet, close the whole dialog
        if (characterImages.length === 0 && !editingCharacter) {
            setCharacterDialogOpen(false);
        }
    };

    const removeCharacterImage = (id) => {
        setCharacterImages(prev => prev.filter(img => img.id !== id));
    };

    const normalizeCharacterImages = async (images) => {
        if (images.length <= 1) return images.map(i => i.dataUrl);
        const minHeight = Math.min(...images.map(i => i.height));
        const targetWidth = Math.round(minHeight * (3 / 4));
        return Promise.all(images.map(img => {
            if (img.width === targetWidth && img.height === minHeight) return Promise.resolve(img.dataUrl);
            return new Promise((resolve) => {
                const canvas = document.createElement('canvas');
                canvas.width = targetWidth;
                canvas.height = minHeight;
                const ctx = canvas.getContext('2d');
                const image = new Image();
                image.onload = () => {
                    ctx.drawImage(image, 0, 0, targetWidth, minHeight);
                    resolve(canvas.toDataURL('image/jpeg', 0.9));
                };
                image.src = img.dataUrl;
            });
        }));
    };

    const openEditDialog = (character) => {
        setEditingCharacter(character);
        const isCustomRole = character.role && !CHARACTER_ROLE_OPTIONS.includes(character.role);
        setCharacterForm({
            name: character.name || '',
            role: character.role || '',
            description: character.description || '',
        });
        setCharacterRoleCustom(isCustomRole);
        setCharacterImages([]);
        setCroppingImageSrc(null);
        setCharacterDialogOpen(true);
    };

    const closeCharacterDialog = () => {
        setCharacterDialogOpen(false);
        setEditingCharacter(null);
        setCharacterForm({ name: '', role: '', description: '' });
        setCharacterImages([]);
        setCroppingImageSrc(null);
        setCharacterRoleCustom(false);
    };

    const handleCharacterSave = async () => {
        setIsCharacterSaving(true);
        try {
            if (editingCharacter) {
                await onCharacterUpdate(editingCharacter.character_id, {
                    name: characterForm.name || undefined,
                    role: characterForm.role || undefined,
                    description: characterForm.description || undefined,
                });
            } else if (characterImages.length > 0) {
                const normalizedDataUrls = await normalizeCharacterImages(characterImages);
                await onCharacterUpload(normalizedDataUrls, {
                    name: characterForm.name || undefined,
                    role: characterForm.role || undefined,
                    description: characterForm.description || undefined,
                });
            }
            closeCharacterDialog();
        } catch {
            // Error toasts are handled by parent handlers
        } finally {
            setIsCharacterSaving(false);
        }
    };

    const [mediaTab, setMediaTab] = useState('photos');
    const selectedReviewsCount = selectedReviews.length;

    // Reset tab if reviews are hidden
    React.useEffect(() => {
        if (hideReviews && mediaTab === 'reviews') setMediaTab('photos');
        if (hideCharacters && mediaTab === 'characters') setMediaTab('photos');
    }, [hideCharacters, hideReviews, mediaTab]);

    useEffect(() => {
        setPreviewImageMeta(null);
    }, [previewPhoto?.url]);

    // ── Right Panel Content (Preview/Media) ──────────────────────────────────
    const RightPanelContent = (
        <div className="w-full h-full flex flex-col overflow-hidden">
            {/* Tab Header */}
            <div className="flex items-center justify-between px-6 pt-2 pb-2 border-b shrink-0">
                <div className="flex gap-1">
                    <button
                        onClick={() => setMediaTab('photos')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            mediaTab === 'photos'
                                ? 'bg-coral/10 text-coral'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                        )}
                    >
                        <ImageIcon className="size-4" />
                        Photos
                        <span className={cn(
                            'text-xs px-1.5 py-0.5 rounded-full',
                            mediaTab === 'photos' ? 'bg-coral/20 text-coral' : 'bg-muted text-muted-foreground'
                        )}>
                            {selectedPhotosCount}/{photos.length}
                        </span>
                    </button>
                    {reviews.length > 0 && !hideReviews && (
                        <button
                            onClick={() => setMediaTab('reviews')}
                            className={cn(
                                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                mediaTab === 'reviews'
                                    ? 'bg-teal/10 text-teal'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                            )}
                        >
                            <MessageSquare className="size-4" />
                            Reviews
                            <span className={cn(
                                'text-xs px-1.5 py-0.5 rounded-full',
                                mediaTab === 'reviews' ? 'bg-teal/20 text-teal' : 'bg-muted text-muted-foreground'
                            )}>
                                {selectedReviewsCount}/{reviews.length}
                            </span>
                        </button>
                    )}
                    {!hideCharacters && (
                        <button
                            onClick={() => setMediaTab('characters')}
                            className={cn(
                                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                mediaTab === 'characters'
                                    ? 'bg-coral/10 text-coral'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                            )}
                        >
                            <Users className="size-4" />
                            Characters
                            <span className={cn(
                                'text-xs px-1.5 py-0.5 rounded-full',
                                mediaTab === 'characters' ? 'bg-coral/20 text-coral' : 'bg-muted text-muted-foreground'
                            )}>
                                {characters.length}/3
                            </span>
                        </button>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                />
                <input
                    ref={characterFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCharacterFileSelect}
                    className="hidden"
                />
                {mediaTab === 'photos' && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingPhoto}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        {isUploadingPhoto ? 'Uploading...' : 'Add Photo'}
                    </Button>
                )}
                {mediaTab === 'characters' && characters.length < 3 && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => characterFileInputRef.current?.click()}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Character
                    </Button>
                )}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {mediaTab === 'photos' && (
                    <>
                        <p className="text-sm text-muted-foreground mb-4">
                            {selectedPhotosCount === 0
                                ? 'Select or upload photos, or let AI generate the visuals for your ad.'
                                : `Select, upload, or let AI generate the photos for your ad — ${selectedPhotosCount} selected.`}
                        </p>
                        <SelectablePhotoGrid
                            photos={photos}
                            onToggle={onPhotoToggle}
                            onDelete={onPhotoDelete}
                            onPreview={setPreviewPhoto}
                            photoDeleteLoading={photoDeleteLoading}
                            emptyMessage="No photos available yet"
                        />
                    </>
                )}

                {mediaTab === 'reviews' && (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground mb-4">
                            Selected reviews will help shape your video's narrative ({selectedReviewsCount} selected)
                        </p>
                        <SelectableReviewsList
                            reviews={reviews}
                            selectedReviewIds={selectedReviews}
                            onToggle={onReviewToggle}
                        />
                    </div>
                )}

                {!hideCharacters && mediaTab === 'characters' && (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            Add people to feature in your video (optional, up to 3)
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            {characters.map((char) => (
                                <CharacterCard
                                    key={char.character_id}
                                    character={char}
                                    onEdit={openEditDialog}
                                    onDelete={onCharacterDelete}
                                />
                            ))}
                            {characters.length < 3 && (
                                <button
                                    onClick={() => characterFileInputRef.current?.click()}
                                    className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 hover:bg-coral/5 transition-all flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-coral"
                                >
                                    <Plus className="h-6 w-6" />
                                    <span className="text-xs font-medium">Add Character</span>
                                </button>
                            )}
                        </div>
                        {characters.length === 0 && (
                            <p className="text-xs text-muted-foreground text-center mt-2">
                                Upload photos of brand ambassadors, spokespersons, or other people to feature in your video.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <CreativesLayout
            fullHeight={layoutFullHeight}
            stickyOffsetClassName={layoutStickyOffsetClassName}
            preview={
                <div className="hidden lg:flex w-full h-full flex-col">
                    {RightPanelContent}
                </div>
            }
            mobilePreview={null} // Hide sticky header on mobile
        >
            <div className="mx-auto max-w-md space-y-8 pb-28">
                {/* Intro with back */}
                <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            {!hideBackButton && (
                                <button
                                    onClick={onBack}
                                    className="mb-3 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Back
                                </button>
                            )}
                            <h2 className="text-2xl font-semibold tracking-tight text-foreground mb-2">{title}</h2>
                            <p className="text-muted-foreground">
                                {description || `Customize the style and content for ${realBusinessData?.name}`}
                            </p>
                        </div>
                        {headerActions ? (
                            <div className="hidden shrink-0 items-center gap-2 lg:flex">
                                {headerActions}
                            </div>
                        ) : null}
                    </div>
                    {headerActions ? (
                        <div className="flex flex-wrap items-center gap-2 lg:hidden">
                            {headerActions}
                        </div>
                    ) : null}
                </div>
                {/* Mobile Media Library (Visible only on mobile/tablet) */}
                <div className="lg:hidden border rounded-xl p-4 bg-muted/20">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                            <ImageIcon className="size-5 text-coral" />
                            Media Selection
                        </h3>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingPhoto}
                            className="h-8 bg-white"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Add
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                        {photos.map((photo, idx) => {
                            const photoUrl = photo.data_url || photo.url || photo.photo_url;
                            return (
                                <div
                                    key={photo.photo_id || idx}
                                    className={cn(
                                        'relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all duration-200',
                                        photo.selected
                                            ? 'border-coral ring-4 ring-coral/15 shadow-md'
                                            : 'border-slate-200 bg-slate-100'
                                    )}
                                    onClick={() => onPhotoToggle(photo.photo_id, idx)}
                                >
                                    {photoUrl && (
                                        <img
                                            src={photoUrl}
                                            alt=""
                                            className={cn(
                                                'h-full w-full object-cover transition-all duration-200',
                                                photo.selected ? 'scale-[1.01]' : 'opacity-80 saturate-75'
                                            )}
                                        />
                                    )}
                                    <div className={cn(
                                        'pointer-events-none absolute inset-0',
                                        photo.selected ? 'bg-coral/10' : ''
                                    )}>
                                    </div>
                                    <button
                                        className="absolute top-2 right-2 z-10 flex h-7 items-center justify-center gap-1 rounded-full border border-white/30 bg-slate-950/80 px-2.5 text-white shadow-sm backdrop-blur-sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewPhoto({ ...photo, url: photoUrl, index: idx });
                                        }}
                                    >
                                        <ImageIcon className="h-3 w-3" />
                                        <span className="text-[11px] font-medium">Preview</span>
                                    </button>
                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-end p-2">
                                        {photo.selected ? (
                                            <div className="inline-flex items-center gap-1 rounded-full bg-coral px-2 py-1 text-[10px] font-semibold text-white shadow-sm">
                                                <CheckCircle2 className="h-3 w-3" />
                                                <span>Selected</span>
                                            </div>
                                        ) : (
                                            <div className="h-5 w-5 rounded-full border border-gray-200 bg-white/90 opacity-50">
                                                <CheckCircle2 className="h-3 w-3 text-transparent" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {photos.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            No photos available. Use desktop to manage uploads fully.
                        </div>
                    )}

                    {/* Mobile Characters */}
                    {!hideCharacters && (
                        <div className="mt-6 pt-4 border-t">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                                    <Users className="size-4 text-coral" />
                                    Characters
                                    <span className="text-xs text-muted-foreground font-normal">({characters.length}/3)</span>
                                </h4>
                                {characters.length < 3 && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => characterFileInputRef.current?.click()}
                                        className="h-7 text-xs bg-white"
                                    >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Add
                                    </Button>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {characters.map((char) => (
                                    <CharacterCard
                                        key={char.character_id}
                                        character={char}
                                        onEdit={openEditDialog}
                                        onDelete={onCharacterDelete}
                                        compact
                                    />
                                ))}
                                {characters.length === 0 && (
                                    <button
                                        onClick={() => characterFileInputRef.current?.click()}
                                        className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 transition-all flex flex-col items-center justify-center gap-1 text-muted-foreground"
                                    >
                                        <Plus className="h-5 w-5" />
                                        <span className="text-[10px]">Add</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {focusedMode ? (
                    /* ── Focused Mode: Accordion layout ────────────────── */
                    <>
                        <AspectRatioSelector
                            value={aspectRatio}
                            onChange={setAspectRatio}
                            options={effectiveAspectRatios}
                            disabled={videoSettingsDisabled}
                        />

                        {/* Duration pills (always visible, compact) */}
                        {!hideDuration && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Duration</Label>
                                <div className="flex flex-wrap gap-2">
                                    {effectiveDurations.map((sec) => (
                                        <button
                                            key={sec}
                                            disabled={videoSettingsDisabled}
                                            className={cn(
                                                'py-2.5 px-5 rounded-xl border-2 text-sm font-medium transition-all',
                                                duration === sec
                                                    ? 'border-coral bg-coral/5 text-coral shadow-sm'
                                                    : 'border-border hover:border-muted-foreground/30 text-muted-foreground',
                                                videoSettingsDisabled && 'cursor-not-allowed opacity-60'
                                            )}
                                            onClick={() => setDuration(sec)}
                                        >
                                            {sec}s
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Narration voice picker — only when the parent wires voice state */}
                        {setVoiceId && (
                            <VoicePicker
                                value={voiceId}
                                onChange={setVoiceId}
                                disabled={videoSettingsDisabled || silentVideo}
                            />
                        )}

                        {/* Accordion sections */}
                        <Accordion type="multiple" className="border rounded-xl overflow-hidden">
                            {/* Campaign Brief */}
                            <AccordionItem value="campaign-brief" className="px-4">
                                <AccordionTrigger className="hover:no-underline">
                                    <div className="flex flex-col items-start gap-0.5">
                                        <span className="text-sm font-semibold">Anything to tell the AI?</span>
                                        <span className="text-xs text-muted-foreground font-normal">{campaignBriefSummary}</span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <CampaignBriefSection
                                        campaignBrief={campaignBrief}
                                        onCampaignBriefChange={setCampaignBrief}
                                        campaignOptions={campaignOptions}
                                        notesRows={3}
                                        topSlot={campaignBriefTopSlot}
                                    />
                                </AccordionContent>
                            </AccordionItem>

                            {/* Characters */}
                            {!hideCharacters && (
                                <AccordionItem value="characters" className="px-4">
                                    <AccordionTrigger className="hover:no-underline">
                                        <div className="flex flex-col items-start gap-0.5">
                                            <span className="text-sm font-semibold">
                                                Characters <span className="font-normal text-muted-foreground">(Optional)</span>
                                            </span>
                                            <span className="text-xs text-muted-foreground font-normal">{charactersSummary}</span>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className="text-sm text-muted-foreground mb-3">
                                            Add people like brand ambassadors or spokespersons to feature in your video.
                                        </p>
                                        <div className="grid grid-cols-3 gap-3">
                                            {characters.map((char) => (
                                                <CharacterCard
                                                    key={char.character_id}
                                                    character={char}
                                                    onEdit={openEditDialog}
                                                    onDelete={onCharacterDelete}
                                                    compact
                                                />
                                            ))}
                                            {characters.length < 3 && (
                                                <button
                                                    onClick={() => characterFileInputRef.current?.click()}
                                                    className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 hover:bg-coral/5 transition-all flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:text-coral"
                                                >
                                                    <Plus className="h-5 w-5" />
                                                    <span className="text-xs font-medium">Add</span>
                                                </button>
                                            )}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            )}

                            {/* Logo */}
                            <AccordionItem value="logo" className="px-4">
                                <AccordionTrigger className="hover:no-underline">
                                    <div className="flex items-center gap-3">
                                        {logoDataUrl && (
                                            <div className="w-8 h-8 rounded border bg-muted/20 flex items-center justify-center overflow-hidden shrink-0 p-0.5">
                                                <img src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
                                            </div>
                                        )}
                                        <div className="flex flex-col items-start gap-0.5">
                                            <span className="text-sm font-semibold">
                                                Logo <span className="font-normal text-muted-foreground">(Optional)</span>
                                            </span>
                                            <span className="text-xs text-muted-foreground font-normal">{logoSummaryText}</span>
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <LogoOverlaySection
                                        logoSrc={logoDataUrl}
                                        onUpload={onLogoUpload}
                                        onDelete={onLogoDelete}
                                        logoOverlayEnabled={logoOverlayEnabled}
                                        onLogoOverlayEnabledChange={setLogoOverlayEnabled}
                                        logoOverlayPosition={logoOverlayPosition}
                                        onLogoOverlayPositionChange={setLogoOverlayPosition}
                                        logoOverlayOpacity={logoOverlayOpacity}
                                        onLogoOverlayOpacityChange={setLogoOverlayOpacity}
                                        {...logoSectionProps}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>

                        {/* Errors */}
                        {apiError && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-200">{apiError}</div>}
                        {campaignOptionsError && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-200">{campaignOptionsError}</div>}
                    </>
                ) : (
                    /* ── Standard Mode: Flat sections ──────────────────── */
                    <>
                        {/* Section 1: Video Settings */}
                        <section className="space-y-6">
                            <h3 className="text-lg font-semibold border-b pb-2">Video Settings</h3>

                            <AspectRatioSelector
                                value={aspectRatio}
                                onChange={setAspectRatio}
                                options={effectiveAspectRatios}
                                disabled={videoSettingsDisabled}
                            />

                            {!hideDuration && (
                                <div className="space-y-3">
                                    <Label>Duration</Label>
                                    <div className={cn('grid gap-3', {
                                        'grid-cols-2': effectiveDurations.length === 2,
                                        'grid-cols-3': effectiveDurations.length === 3,
                                        'grid-cols-4': effectiveDurations.length >= 4,
                                    })}>
                                        {effectiveDurations.map((sec) => (
                                            <button
                                                key={sec}
                                                disabled={videoSettingsDisabled}
                                                className={cn(
                                                    'py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all',
                                                    duration === sec
                                                        ? 'border-coral bg-coral/5 text-coral shadow-sm'
                                                        : 'border-border hover:border-muted-foreground/30 text-muted-foreground',
                                                    videoSettingsDisabled && 'cursor-not-allowed opacity-60'
                                                )}
                                                onClick={() => setDuration(sec)}
                                            >
                                                {sec}s
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Narration voice picker — only when the parent wires voice state */}
                            {setVoiceId && (
                                <VoicePicker
                                    value={voiceId}
                                    onChange={setVoiceId}
                                    disabled={videoSettingsDisabled || silentVideo}
                                />
                            )}
                        </section>

                        {/* Logo Section */}
                        <section className="space-y-4">
                            <h3 className="text-lg font-semibold border-b pb-2">Logo</h3>
                            <LogoOverlaySection
                                logoSrc={logoDataUrl}
                                onUpload={onLogoUpload}
                                onDelete={onLogoDelete}
                                logoOverlayEnabled={logoOverlayEnabled}
                                onLogoOverlayEnabledChange={setLogoOverlayEnabled}
                                logoOverlayPosition={logoOverlayPosition}
                                onLogoOverlayPositionChange={setLogoOverlayPosition}
                                logoOverlayOpacity={logoOverlayOpacity}
                                onLogoOverlayOpacityChange={setLogoOverlayOpacity}
                                {...logoSectionProps}
                            />
                        </section>

                        {/* Characters Section (Optional) */}
                        {!hideCharacters && (
                            <section className="space-y-4 hidden lg:block">
                                <div className="flex items-center justify-between border-b pb-2">
                                    <h3 className="text-lg font-semibold flex items-center gap-2">
                                        Characters
                                        <span className="text-sm font-normal text-muted-foreground">(Optional)</span>
                                    </h3>
                                    <span className="text-xs text-muted-foreground">{characters.length}/3</span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Add people like brand ambassadors or spokespersons to feature in your video.
                                </p>
                                <div className="grid grid-cols-3 gap-3">
                                    {characters.map((char) => (
                                        <CharacterCard
                                            key={char.character_id}
                                            character={char}
                                            onEdit={openEditDialog}
                                            onDelete={onCharacterDelete}
                                            compact
                                        />
                                    ))}
                                    {characters.length < 3 && (
                                        <button
                                            onClick={() => characterFileInputRef.current?.click()}
                                            className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 hover:bg-coral/5 transition-all flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:text-coral"
                                        >
                                            <Plus className="h-5 w-5" />
                                            <span className="text-xs font-medium">Add</span>
                                        </button>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Section 2: Campaign Details */}
                        <section className="space-y-6">
                            <h3 className="text-lg font-semibold border-b pb-2">Campaign Details</h3>

                            <CampaignBriefSection
                                campaignBrief={campaignBrief}
                                onCampaignBriefChange={setCampaignBrief}
                                campaignOptions={campaignOptions}
                                topSlot={campaignBriefTopSlot}
                            />

                            {apiError && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-200">{apiError}</div>}
                            {campaignOptionsError && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-200">{campaignOptionsError}</div>}
                        </section>
                    </>
                )}

                {/* "Tone of the ad" (script mode) is hidden — every ad uses Creative
                    mode by default. Re-enable by uncommenting the selector below. */}
                {/* <ScriptModeSelector value={scriptMode} onChange={setScriptMode} disabled={videoSettingsDisabled} /> */}

            </div>

            {/* Sticky Generate Button */}
            <div className="sticky bottom-0 z-30 -mx-6 mt-8 border-t bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
                <div className="max-w-md mx-auto">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="block">
                                    <Button
                                        onClick={onGenerate}
                                        disabled={generateDisabled}
                                        size="lg"
                                        className="w-full shadow-lg shadow-primary/20"
                                    >
                                        {isGenerating ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                {generateButtonLoadingLabel || generateButtonLabel || 'Generating...'}
                                            </>
                                        ) : (
                                            generateButtonLabel || 'Generate Video'
                                        )}
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            {generateTooltip && <TooltipContent>{generateTooltip}</TooltipContent>}
                        </Tooltip>
                    </TooltipProvider>
                    {isPaymentRequired() && (
                        <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-teal" />
                            100% money-back guarantee — not happy with your video? We'll refund you.
                        </p>
                    )}
                </div>
            </div>

            {/* Photo Preview Dialog */}
            <Dialog open={!!previewPhoto} onOpenChange={(v) => !v && setPreviewPhoto(null)}>
                <DialogContent
                    showCloseButton={false}
                    className="max-h-[calc(100vh-1.5rem)] max-w-[95vw] overflow-hidden border border-slate-200 bg-white p-0 shadow-lg sm:max-h-[calc(100vh-3rem)] sm:max-w-6xl"
                >
                    {previewPhoto && (
                        <>
                            <DialogHeader className="sr-only">
                                <DialogTitle>Photo preview</DialogTitle>
                                <DialogDescription>
                                    Review this business photo, inspect its details, and decide whether to include it in the video.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid h-full max-h-[calc(100vh-1.5rem)] grid-cols-1 overflow-y-auto md:max-h-[calc(100vh-3rem)] md:grid-cols-[minmax(0,1.9fr)_360px] md:overflow-hidden">
                            <div className="bg-slate-100 p-4 sm:p-5">
                                <div className="flex items-start justify-center rounded-2xl border border-slate-200 bg-white p-4 md:h-full md:min-h-[360px] md:items-center">
                                    <img
                                        src={previewPhoto.url}
                                        alt="Photo preview"
                                        className="max-h-[48vh] w-full rounded-xl object-contain md:max-h-[76vh]"
                                        onLoad={(event) => {
                                            const { naturalWidth, naturalHeight } = event.currentTarget;
                                            setPreviewImageMeta({
                                                width: naturalWidth,
                                                height: naturalHeight,
                                                format: inferPhotoFormat(previewPhoto),
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex min-h-full flex-col border-t border-slate-200 bg-white p-5 md:border-t-0 md:border-l md:overflow-y-auto">
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
                                            Photo preview
                                        </p>
                                        <p className="mt-2 text-lg font-semibold text-slate-950">
                                            {previewPhoto.selected ? 'Selected for video' : 'Available to select'}
                                        </p>
                                        <p className="mt-1 text-sm leading-6 text-slate-600">
                                            Review this business photo before deciding whether to include it in the video.
                                        </p>
                                    </div>

                                    <div className="space-y-2 text-sm text-slate-600">
                                        {previewImageMeta?.width && previewImageMeta?.height ? (
                                            <p>Dimensions: {previewImageMeta.width} × {previewImageMeta.height}</p>
                                        ) : null}
                                        <p>Format: {previewImageMeta?.format || inferPhotoFormat(previewPhoto)}</p>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                                            Business photo
                                        </div>
                                        {previewPhoto.selected ? (
                                            <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                                                Included in video
                                            </div>
                                        ) : (
                                            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                                                Not selected yet
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="sticky bottom-0 mt-6 space-y-3 border-t border-slate-200 bg-white pt-4 md:mt-auto md:border-t-0 md:bg-transparent md:pt-0">
                                    <Button
                                        className={previewPhoto.selected ? 'w-full bg-slate-950 text-white hover:bg-slate-800' : 'w-full bg-coral text-white hover:bg-coral/90'}
                                        onClick={() => {
                                            onPhotoToggle(previewPhoto.photo_id, previewPhoto.index);
                                            setPreviewPhoto(prev => ({ ...prev, selected: !prev.selected }));
                                        }}
                                    >
                                        {previewPhoto.selected ? 'Remove from video' : 'Select for video'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setPreviewPhoto(null)}
                                        className="w-full bg-white"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Character Upload/Edit Dialog */}
            <Dialog open={characterDialogOpen} onOpenChange={(v) => !v && closeCharacterDialog()}>
                <DialogContent className="sm:max-w-[420px]">
                    {croppingImageSrc ? (
                        /* Cropping mode */
                        <>
                            <DialogHeader>
                                <DialogTitle>Crop Portrait</DialogTitle>
                            </DialogHeader>
                            <PortraitCropper
                                imageSrc={croppingImageSrc}
                                onCropComplete={handleCropComplete}
                                onCancel={handleCropCancel}
                            />
                        </>
                    ) : (
                        /* Form mode */
                        <>
                            <DialogHeader>
                                <DialogTitle>
                                    {editingCharacter ? 'Edit Character' : 'Add Character Reference'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-2">
                                {/* Image thumbnails */}
                                {editingCharacter ? (
                                    /* Read-only thumbnails for existing character */
                                    <div className="grid grid-cols-3 gap-2">
                                        {(editingCharacter.data_urls || [editingCharacter.image_url || editingCharacter.data_url].filter(Boolean)).map((url, idx) => (
                                            <div key={idx} className="aspect-[3/4] rounded-lg overflow-hidden border bg-muted/20">
                                                <img src={url} alt={`Reference ${idx + 1}`} className="w-full h-full object-cover" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* Editable thumbnails for new character */
                                    <>
                                        <input
                                            ref={addPhotoInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleCharacterFileSelect}
                                            className="hidden"
                                        />
                                        <div className="grid grid-cols-3 gap-2">
                                            {characterImages.map((img) => (
                                                <div key={img.id} className="relative aspect-[3/4] rounded-lg overflow-hidden border bg-muted/20 group">
                                                    <img src={img.dataUrl} alt="Cropped reference" className="w-full h-full object-cover" />
                                                    <button
                                                        className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                                        onClick={() => removeCharacterImage(img.id)}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            ))}
                                            {characterImages.length < 3 && (
                                                <button
                                                    onClick={() => addPhotoInputRef.current?.click()}
                                                    className="aspect-[3/4] rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 hover:bg-coral/5 transition-all flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-coral"
                                                >
                                                    <Plus className="h-5 w-5" />
                                                    <span className="text-[10px] font-medium">Add Photo</span>
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground text-center">
                                            1 required &bull; 3 max
                                        </p>
                                    </>
                                )}

                                <div className="space-y-2">
                                    <Label>Name <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                                    <Input
                                        placeholder="e.g., Maria"
                                        value={characterForm.name}
                                        onChange={(e) => setCharacterForm(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Role <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                                    {characterRoleCustom ? (
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder="Enter custom role"
                                                value={characterForm.role}
                                                onChange={(e) => setCharacterForm(prev => ({ ...prev, role: e.target.value }))}
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="shrink-0"
                                                onClick={() => {
                                                    setCharacterRoleCustom(false);
                                                    setCharacterForm(prev => ({ ...prev, role: '' }));
                                                }}
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Select
                                            value={characterForm.role || '_placeholder'}
                                            onValueChange={(value) => {
                                                if (value === '_other') {
                                                    setCharacterRoleCustom(true);
                                                    setCharacterForm(prev => ({ ...prev, role: '' }));
                                                } else if (value === '_placeholder') {
                                                    setCharacterForm(prev => ({ ...prev, role: '' }));
                                                } else {
                                                    setCharacterForm(prev => ({ ...prev, role: value }));
                                                }
                                            }}
                                        >
                                            <SelectTrigger className="h-10">
                                                <SelectValue placeholder="Select a role..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="_placeholder" disabled className="text-muted-foreground">
                                                    Select a role...
                                                </SelectItem>
                                                {CHARACTER_ROLE_OPTIONS.map((role) => (
                                                    <SelectItem key={role} value={role}>{role}</SelectItem>
                                                ))}
                                                <SelectItem value="_other" className="text-coral">Other...</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Description <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                                    <Textarea
                                        placeholder="e.g., Energetic, always smiling, wears brand colors"
                                        value={characterForm.description}
                                        onChange={(e) => setCharacterForm(prev => ({ ...prev, description: e.target.value }))}
                                        rows={2}
                                        className="resize-none"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={closeCharacterDialog}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleCharacterSave}
                                    disabled={isCharacterSaving || (!editingCharacter && characterImages.length === 0)}
                                >
                                    {isCharacterSaving ? 'Saving...' : 'Save'}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </CreativesLayout>
    );
};

export default ConfigureStep;
